import { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { ParagraphText, UBButton, UBInput } from "../../../Libraries";
import TabAutoComplete from "../../../Libraries/TabAutoComplete";
import {
  NadraIcon,
  RaastApprovedLogo,
  TrustShieldIcon,
} from "../../../assets/icons/icon";
import { LanguageContext } from "../../../context/LanguageContext";
import styles from "./styles.module.css";

const ACCOUNT_NUMBER_BILLERS = ["KESC"];

export const isAccountNumberBiller = (value) =>
  ACCOUNT_NUMBER_BILLERS.includes(value);

export const PHONE_NUMBER_PATTERN =
  /^0?3[0-9]{2}(?!1234567)(?!1111111)(?!7654321)[0-9]{7}$/;

export const isValidBillPhoneNumber = (value = "") =>
  value.length === (value.startsWith("0") ? 11 : 10) &&
  PHONE_NUMBER_PATTERN.test(value);

const BILLER_OPTIONS = [
  "FESCO",
  "IESCO",
  "MEPCO",
  "PESCO",
  "HESCO",
  "SEPCO",
  "QESCO",
  "GEPCO",
  "LESCO",
  "KESC",
].map((name) => ({
  label: name === "KESC" ? "K Electric" : name,
  value: name,
}));

export const getBillerLabel = (value) =>
  BILLER_OPTIONS.find((option) => option.value === value)?.label || value;

const BillSearchForm = ({
  initialReferenceValue = "",
  initialPhoneValue = "",
  initialPhoneError = "",
  initialBiller = "",
  error = "",
  loading = false,
  onSearch = () => {},
  onBillerSelect = () => {},
}) => {
  const [I18n] = useContext(LanguageContext);
  const [referenceValue, setReferenceValue] = useState(initialReferenceValue);
  const [phoneValue, setPhoneValue] = useState(initialPhoneValue);
  const [billerValue, setBillerValue] = useState(initialBiller);
  const [phoneError, setPhoneError] = useState(initialPhoneError);
  const [errorMessage, setErrorMessage] = useState(error);

  useEffect(() => {
    setErrorMessage(error);
  }, [error]);

  const TRUST_BADGES = [
    { Icon: NadraIcon, text: I18n.powered_by_nadra },
    { Icon: RaastApprovedLogo, text: I18n.licensed_by_sbp_in_principle },
    { Icon: TrustShieldIcon, text: I18n.trusted_by_5_3_million_users },
  ];

  const billerLabel = getBillerLabel(billerValue);
  const referenceFieldLabel = isAccountNumberBiller(billerValue)
    ? I18n.account_no
    : I18n.reference_no;

  const handleReferenceChange = (_, value) => {
    setReferenceValue(value);
    setErrorMessage("");
  };

  const handlePhoneChange = (_, value) => {
    const maxLength = value.startsWith("0") ? 11 : 10;
    if (value.length > maxLength) return;

    if (value.length === maxLength && !isValidBillPhoneNumber(value)) {
      setPhoneError(I18n.phone_number_format);
    } else {
      setPhoneError("");
    }

    setPhoneValue(value);
    setErrorMessage("");
  };

  const handleBillerSelect = (item) => {
    const nextBiller = item?.value || "";
    setBillerValue(nextBiller);
    setReferenceValue("");
    setErrorMessage("");
    onBillerSelect(nextBiller);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch({ referenceValue, phoneValue, billerValue });
  };

  const isPhoneValid = isValidBillPhoneNumber(phoneValue);
  const isReferenceValid = referenceValue.length >= 5;
  const isBillerValid = Boolean(billerValue);
  const isSearchDisabled =
    !isReferenceValid || !isPhoneValid || !isBillerValid || loading;

  return (
    <Box className={styles.wrapper}>
      <ParagraphText
        className={`fontSize24 fontWeight500 newFontColor ${styles.heading}`}
      >
        {I18n.instantly_check_your_bill}
      </ParagraphText>
      <ParagraphText
        className={`fontSize14 newLightColor ${styles.subheading}`}
      >
        {I18n.enter_bill_details_below}
      </ParagraphText>

      <Box className={styles.formSection}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <TabAutoComplete
            className={styles.billerField}
            id="bill_search_biller"
            list={BILLER_OPTIONS}
            label={I18n.select_biller}
            placeholder={I18n.select_biller}
            value={billerLabel}
            handleSelect={handleBillerSelect}
          />

          <Box
            className={`${styles.additionalFields} ${
              billerValue ? styles.additionalFieldsVisible : ""
            }`}
          >
            <Box className={styles.additionalFieldsInner}>
              <UBInput
                inputId="bill_search_reference_input"
                label={referenceFieldLabel}
                placeholder={referenceFieldLabel}
                maxLength={30}
                value={referenceValue}
                error={{ text: errorMessage, show: !!errorMessage }}
                onChange={handleReferenceChange}
                autoFocus={!!billerValue}
                onlyNumbers
                isLeadingZero={false}
              />

              <UBInput
                inputId="bill_search_phone_input"
                label={I18n.enter_phone_number}
                placeholder={I18n.enter_phone_number}
                value={phoneValue}
                error={{ text: phoneError, show: !!phoneError }}
                onlyNumbers
                isLeadingZero={false}
                maxLength={11}
                onChange={handlePhoneChange}
              />
            </Box>
          </Box>

          <UBButton
            id="bill_search_submit_btn"
            type="submit"
            title={I18n.search_bill}
            loading={loading}
            disabled={isSearchDisabled}
            className={`ubPrimaryButton textUppercase ${styles.searchButton}`}
          />
        </form>

        <Box className={styles.footer}>
          {TRUST_BADGES.map(({ Icon, text }) => (
            <Box key={text} className={styles.footerBadge}>
              <Icon />
              <ParagraphText className="newLightColor fontSize14">
                {text}
              </ParagraphText>
            </Box>
          ))}
        </Box>
      </Box>

      {/* <Box className={styles.formSection}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <TabAutoComplete
            className={styles.billerField}
            id="bill_search_biller"
            list={BILLER_OPTIONS}
            label={I18n.select_biller}
            placeholder={I18n.select_biller}
            value={billerLabel}
            handleSelect={handleBillerSelect}
          />

          {billerValue && (
            <>
              <UBInput
                inputId="bill_search_reference_input"
                label={referenceFieldLabel}
                placeholder={referenceFieldLabel}
                maxLength={30}
                value={referenceValue}
                error={{ text: errorMessage, show: !!errorMessage }}
                onChange={handleReferenceChange}
                autoFocus
              />

              <UBInput
                inputId="bill_search_phone_input"
                label={I18n.enter_phone_number}
                placeholder={I18n.enter_phone_number}
                value={phoneValue}
                error={{ text: phoneError, show: !!phoneError }}
                onlyNumbers
                isLeadingZero={false}
                maxLength={11}
                onChange={handlePhoneChange}
              />
            </>
          )}

          <UBButton
            id="bill_search_submit_btn"
            type="submit"
            title={I18n.search_bill}
            loading={loading}
            disabled={isSearchDisabled}
            className={`ubPrimaryButton textUppercase ${styles.searchButton}`}
          />
        </form>

        <Box className={styles.footer}>
          {TRUST_BADGES.map(({ Icon, text }) => (
            <Box key={text} className={styles.footerBadge}>
              <Icon />
              <ParagraphText className="newLightColor fontSize14">{text}</ParagraphText>
            </Box>
          ))}
        </Box>
      </Box> */}
    </Box>
  );
};

export default BillSearchForm;
